import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { submitSupportTicket } from "../lib/support";

const CATEGORIES = [
    { value: "billing", label: "Billing & payments" },
    { value: "technical", label: "Something's broken" },
    { value: "account", label: "My account / plan" },
    { value: "general", label: "General question" },
];

// Built-in floating support widget (used when no third-party live chat is configured).
// A blocked payment or any question routes here → emails your support inbox via SMTP.
export default function SupportWidget() {
    const [open, setOpen] = useState(false);
    const [category, setCategory] = useState("general");
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [err, setErr] = useState("");

    // Let other parts of the app open the widget (e.g. the payment-failure banner).
    useEffect(() => {
        const onOpen = () => setOpen(true);
        window.addEventListener("cfai:open-support", onOpen);
        return () => window.removeEventListener("cfai:open-support", onOpen);
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        setErr("");
        try {
            await submitSupportTicket({ subject, message, category, pageUrl: window.location.href });
            setSent(true);
            setSubject("");
            setMessage("");
        } catch (e2) {
            setErr(e2.message);
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            {/* Launcher button */}
            <button
                onClick={() => setOpen((o) => !o)}
                aria-label="Get help"
                className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg flex items-center justify-center text-2xl transition-transform hover:scale-105"
            >
                {open ? "×" : "💬"}
            </button>

            {open && (
                <div className="fixed bottom-24 right-5 z-[60] w-[92vw] max-w-sm rounded-2xl border border-gray-200 dark:border-gray-700/70 bg-white dark:bg-[#23263a] shadow-2xl overflow-hidden">
                    <div className="bg-indigo-600 px-4 py-3 text-white">
                        <p className="font-bold">We're here to help 👋</p>
                        <p className="text-xs text-indigo-100">Send us a message — we reply by email, usually within a few hours.</p>
                    </div>

                    {sent ? (
                        <div className="p-5 text-center">
                            <div className="text-3xl mb-2">✅</div>
                            <p className="font-semibold text-gray-900 dark:text-white">Message sent!</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Check your inbox for a confirmation. We'll be in touch shortly.</p>
                            <button onClick={() => { setSent(false); setOpen(false); }} className="mt-4 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold">Done</button>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="p-4 space-y-3">
                            <div className="flex flex-wrap gap-2">
                                <Link to="/settings/billing" onClick={() => setOpen(false)} className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 no-underline text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#2c2f44]">Fix billing</Link>
                                <Link to="/support" onClick={() => setOpen(false)} className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 no-underline text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#2c2f44]">Help & FAQ</Link>
                            </div>

                            <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-700 dark:text-slate-200">
                                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>

                            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject (optional)" className="w-full text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-700 dark:text-slate-200" />

                            <textarea value={message} onChange={(e) => setMessage(e.target.value)} required rows={4} placeholder="How can we help?" className="w-full text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-700 dark:text-slate-200" />

                            {err && <p className="text-xs text-rose-600 dark:text-rose-400">{err}</p>}

                            <button type="submit" disabled={sending || message.trim().length < 5} className="w-full px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50">
                                {sending ? "Sending…" : "Send message"}
                            </button>
                        </form>
                    )}
                </div>
            )}
        </>
    );
}
